/**
 * /app/$tenantSlug/wfh — Work-from-home requests (migration 063).
 *
 * Leave-shaped but BALANCE-FREE: a WFH request never debits a leave
 * balance; an approved range overlays attendance (a WFH day reads 'wfh'
 * not 'absent') server-side. v1 always requires manager approval.
 *
 * This page is the employee surface: submit a WFH range + see your own
 * requests. Managers/HR decide from the WFH approvals route.
 *
 * Half-day WFH (migration 134): on a single date the employee may ask for
 * the first or second half only; the other half is worked at the office
 * (or is a half-day leave of the other half).
 *
 * Work from home Off for the company (migration 167, /me tenant.wfh_mode):
 * the request form is replaced by one line saying so (the server refuses a
 * new request 409 wfh_off); existing requests stay listed.
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import {
  useWFHRequests,
  useCreateWFHRequest,
  useCancelWFHRequest,
} from "@/hooks/use-wfh";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { wfhOffered } from "@/lib/punching";
import { useLeaveDayCount } from "@/hooks/use-leave-depth";
import { PayrollLockedNotice } from "@/components/leave/payroll-locked-notice";
import { halfDayLabel } from "@/lib/leave-format";
import { formatDate, formatDateRange } from "@/lib/format";
import { endDateAfterStartChange } from "@/lib/date-range-follow";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/wfh/")({
  component: WFHRoute,
});

function WFHRoute() {
  const { tenantSlug } = Route.useParams();
  const { isApprover } = useLeaveRoles();
  const mine = useWFHRequests(undefined, "self");
  const create = useCreateWFHRequest();
  const cancel = useCancelWFHRequest();
  const wfhOn = wfhOffered(useAuth().user?.tenant?.wfh_mode);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  // "" = whole day(s); a half is only offered on a single date.
  const [half, setHalf] = useState<"" | "first_half" | "second_half">("");
  const singleDay = !!start && start === end;
  const effectiveHalf = singleDay ? half : "";
  // Same server day count as the leave form — only for its payroll_locked
  // (persona round 2026-10: warn before submit, the server refuses 409).
  const dayCountQ = useLeaveDayCount(start, end, effectiveHalf || undefined);

  async function submit() {
    if (!start || !end) {
      toast.error("Start and end dates are required");
      return;
    }
    try {
      await create.mutateAsync({
        start_date: start,
        end_date: end,
        reason: reason.trim() || undefined,
        ...(effectiveHalf ? { half_day: effectiveHalf } : {}),
      });
      toast.success("WFH request submitted");
      setStart("");
      setEnd("");
      setReason("");
      setHalf("");
    } catch (e) {
      const err = toApiError(e);
      toast.error(
        err.error === "no_working_days"
          ? "The selected range has no working days"
          : err.error === "wfh_overlap"
            ? "You already have a WFH request covering this time"
            : err.error === "wfh_off"
              ? "Work from home is turned off for your company."
              : (err.message ?? "Failed to submit"),
      );
    }
  }

  return (
    <TimeAndLeaveShell
      title="Work From Home"
      description="Plan WFH days. No leave balance is used — your manager approves and your attendance reflects WFH automatically."
      actions={
        isApprover ? (
          <Button asChild variant="outline">
            <Link to="/app/$tenantSlug/approvals" params={{ tenantSlug }} search={{ tab: "wfh" }}>
              Approvals
            </Link>
          </Button>
        ) : undefined
      }
    >
      <LeaveFeatureGate>
        <div className="space-y-6">
          {!wfhOn ? (
            <p className="text-sm text-muted-foreground" data-testid="wfh-off-note">
              Work from home is turned off for your company, so new requests
              can&apos;t be made. Days already approved still count.
            </p>
          ) : (
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>New WFH Request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="s">Start Date</Label>
                  <Input
                    id="s"
                    type="date"
                    value={start}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStart(v);
                      setEnd((cur) => endDateAfterStartChange(v, cur));
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="e">End Date</Label>
                  <Input
                    id="e"
                    type="date"
                    value={end}
                    min={start || undefined}
                    onChange={(e) => setEnd(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="wfh-half">Day Part</Label>
                <Select
                  value={effectiveHalf || "full"}
                  onValueChange={(v) =>
                    setHalf(v === "full" ? "" : (v as "first_half" | "second_half"))
                  }
                  disabled={!singleDay}
                >
                  <SelectTrigger id="wfh-half" className="sm:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Whole Day</SelectItem>
                    <SelectItem value="first_half">First Half Only</SelectItem>
                    <SelectItem value="second_half">Second Half Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">
                  {singleDay
                    ? "For a half day, work the other half at the office (or take it as a half-day leave)."
                    : "Half a day is available when the start and end date are the same."}
                </p>
              </div>
              <div>
                <Label htmlFor="r">Reason (Optional)</Label>
                <Textarea
                  id="r"
                  value={reason}
                  maxLength={2000}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you working from home?"
                />
              </div>
              <p className="text-muted-foreground text-xs">
                No leave balance impact. Weekends and holidays are excluded
                from the day count.
              </p>
              <PayrollLockedNotice lock={dayCountQ.data?.payroll_locked} what="work from home" />
              <Button
                onClick={submit}
                disabled={create.isPending || !!dayCountQ.data?.payroll_locked}
              >
                {create.isPending ? "Submitting…" : "Submit WFH Request"}
              </Button>
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>My WFH Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {mine.data && mine.data.wfh_requests.length > 0 ? (
                <ul className="divide-y text-sm">
                  {mine.data.wfh_requests.map((w) => (
                    <li
                      key={w.id}
                      className="flex items-center justify-between py-2"
                    >
                      <Link
                        to="/app/$tenantSlug/wfh/$id"
                        params={{ tenantSlug, id: String(w.id) }}
                        className="inline-flex items-center hover:underline pointer-coarse:min-h-11"
                      >
                        {w.half_day
                          ? `${formatDate(w.start_date)} · ${halfDayLabel(w.half_day)}`
                          : formatDateRange(w.start_date, w.end_date)}{" "}
                        ({w.total_days} day
                        {w.total_days === 1 ? "" : "s"})
                      </Link>
                      <span className="flex items-center gap-2">
                        <StatusBadge status={w.status} />
                        {(w.status === "pending" ||
                          w.status === "approved") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancel.mutate(w.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No WFH requests yet." />
              )}
            </CardContent>
          </Card>
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
